import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import getter from 'dibs-object-getter';
import tracking from 'dibs-tracking';
import { EnrollmentPrompt } from 'dibs-trade-loyalty-enrollment';

import TpnRewardsDashboard from './components/TpnRewardsDashboard';
import {
    BlacklistedMessage,
    NotEligibleMessage,
    NotLoggedInMessage,
    NotTradeMessage,
} from '../shared/DeniedAccessMessages';
import { tradeLoyaltyStatuses } from '../constants';
import { isEligibleLoyaltyStatus } from '../helpers';

import styles from './RewardsWrapper.scss';

export class RewardsWrapper extends React.Component {
    constructor(props) {
        super(props);
        this.getLoyaltyStatus = this.getLoyaltyStatus.bind(this);
        this.isEligibleOrEnrolled = this.isEligibleOrEnrolled.bind(this);
        this.mainContent = this.mainContent.bind(this);
    }

    componentDidMount() {
        if (this.props.onCallCompleted) {
            this.props.onCallCompleted();
        }
        tracking.trackPageview({
            additional: {
                pageType: 'Other',
                pageSubType: 'Other',
            },
        });
        tracking.trackEvent({
            category: 'trade features',
            action: 'trade rewards',
            label: 'trade rewards module displayed',
            additional: {
                profileStatus: getter(this.props.viewer)
                    .at('user')
                    .at('tradeFirm')
                    .value('profileStatus'),
            },
        });
    }

    getLoyaltyStatus() {
        return getter(this.props.viewer)
            .at('user')
            .at('tradeFirm')
            .at('loyalty')
            .value('status');
    }

    isEligibleOrEnrolled() {
        return [tradeLoyaltyStatuses.ELIGIBLE, tradeLoyaltyStatuses.ENROLLED].includes(
            this.getLoyaltyStatus()
        );
    }

    mainContent() {
        const { viewer } = this.props;
        const loyaltyStatus = this.getLoyaltyStatus();
        const user = getter(viewer).value('user');
        const isTradeUser = getter(user).value('isVerifiedTrade');
        const notLoggedIn = !user;
        const blacklisted = loyaltyStatus === tradeLoyaltyStatuses.BLACKLISTED;
        const enrolled = loyaltyStatus === tradeLoyaltyStatuses.ENROLLED;
        const eligible = isEligibleLoyaltyStatus(loyaltyStatus);

        switch (true) {
            case notLoggedIn:
                return (
                    <div className={styles.flexContainer}>
                        <NotLoggedInMessage />
                    </div>
                );
            case !isTradeUser:
                return (
                    <div className={styles.flexContainer}>
                        <NotTradeMessage />
                    </div>
                );
            case blacklisted:
                return (
                    <div className={styles.flexContainer}>
                        <BlacklistedMessage />
                    </div>
                );
            case eligible:
                return (
                    <div className={styles.enrollmentPromptContainer}>
                        <EnrollmentPrompt
                            enrollmentFormUrl="/trade/loyalty/enrollment/"
                            location="loyalty landing page"
                            user={user}
                            viewer={viewer}
                        />
                    </div>
                );
            case enrolled:
                return (
                    <TpnRewardsDashboard
                        user={user}
                        viewer={viewer}
                        registerDeepLink={this.props.registerDeepLink}
                    />
                );
            default:
                return (
                    <div className={styles.flexContainer}>
                        <NotEligibleMessage />
                    </div>
                );
        }
    }

    render() {
        return <div className={styles.topSection}>{this.mainContent()}</div>;
    }
}

RewardsWrapper.propTypes = {
    registerDeepLink: PropTypes.func,
    onCallCompleted: PropTypes.func,
    relayError: PropTypes.object,
    viewer: PropTypes.object,
};

export default createFragmentContainer(RewardsWrapper, {
    viewer: graphql`
        fragment RewardsWrapper_viewer on Viewer {
            user(userId: $userId) @include(if: $hasUserId) {
                ...TpnRewardsDashboard_user
                ...EnrollmentPrompt_user
                isVerifiedTrade: hasPermission(name: "B_NET_PRICE")
                tradeFirm {
                    profileStatus
                    loyalty {
                        status
                    }
                }
            }
            ...TpnRewardsDashboard_viewer
        }
    `,
});
